import React from 'react';
import PropTypes from 'prop-types';
import {ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis} from 'recharts';
import {find} from 'lodash';
import {granularities} from './../Helper/CurrencyPairGranularitySelector';
import moment from 'moment';

const BigChart = (props) => {
    /**
     * Returns an Array of ticks depending on the displayed data and the granularity setting where the amount of ticks is
     * configured (e.g. 1 year has 12 tick, one for each month, but 1 month has 15 ticks, every other of 30 days).
     * @param granularities
     * @param granularityId
     * @param data
     * @returns {Array}
     */
    const getTicks = (granularities, granularityId, data) => {
        let ticks = [];
        const granularity = find(granularities, {identifier: granularityId});

        if (granularity.hasOwnProperty('getTicksByData')){
            return granularity.getTicksByData(data);
        }

        const tickCount = granularity.ticks || 0;
        const maxTick = granularity.maxTick || data.length;
        const range = Math.round(maxTick / tickCount);
        for (let i=0; i <= tickCount; i++) {
            const index = i * range;
            if (data[index] && data[index]['timestamp']) ticks.push(data[index]['timestamp']);
        }
        return ticks;
    };

    /**
     * Selects the tick formatter for the selected granularity.
     * @param granularities
     * @param granularityId
     * @returns {function(*): *}
     */
    const getTickFormatter = (granularities, granularityId) => {
        const granularity = find(granularities, {identifier: granularityId});
        return granularity ? granularity.tickFormatter : () => { return v => v};
    };

    /**
     * Selects the data formatter or returns a default one.
     * @param granularities
     * @param granularityId
     * @returns {*}
     */
    const getDataFormatter = (granularities, granularityId) => {
        const granularity = find(granularities, {identifier: granularityId});
        return granularity && granularity.formatData ? granularity.formatData : v => v;
    };

    // Optionally format the data, e.g. 1D fills until 12pm
    const formatData = getDataFormatter(granularities, props.granularity);
    let data = formatData(props.chartData);

    // An array of ticks (X-axis of chart)
    const ticks = getTicks(granularities, props.granularity, data);

    // The tickFormatter is a granularity-dependent function that gets passed the length of the data and returns
    // a format-function for the tick labels (e.g. YTD needs different formatters depending on how many days
    // it displays).
    const tickFormatter = getTickFormatter(granularities, props.granularity);

    moment.locale(props.locale);

    return (
        <div className="currency-pair-chart currency-pair-chart--isBig"  role="img" aria-hidden="true">
            <ResponsiveContainer>
                <LineChart data={data} margin={{bottom: 0, top: 10, right: 10, left: 10}}>
                    <CartesianGrid vertical={false} stroke="#4e9bcd" strokeDasharray="5 5"/>
                    <XAxis
                        dataKey="timestamp"
                        axisLine={false}
                        tickLine={false}
                        ticks={ticks}
                        tickFormatter={tickFormatter(moment, data.length)}
                        interval={0}
                        padding={{left: 20, right: 20}}
                        height={40}
                    />
                    <YAxis
                        orientation="right"
                        width={50}
                        tickFormatter={v => v.toFixed(6)}
                        axisLine={false}
                        tickLine={false}
                        domain={['dataMin', 'dataMax']}
                    />
                    <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#ffffff"
                        strokeWidth="1.5"
                        dot={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

BigChart.propTypes = {
    /**
     * the data that should be displayed in the chart view
     */
    chartData: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.number.isRequired,
            timestamp: PropTypes.string,
            unit: PropTypes.string
        })
    ),

    granularity: PropTypes.string
};

export default BigChart;